import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminInvitationsList } from '../components/AdminInvitationsList';
import type { ProjectsListColumn } from '../components/ProjectsListTable';
import { adminApi, type AdminCustomerInvitation } from '../services/api';

const DEFAULT_COLUMN_ORDER = [
  'customerName', 'companyName', 'contactEmail', 'contactPhone', 'registeredBy', 'status', 'createdAt'
];

/** Client invitations (S8): from Etappio staff and from contractors. */
export function AdminCustomerInvitationList() {
  const { t } = useTranslation();

  // Memoized: the shared list rebuilds its columns when these change.
  const ownColumns: ProjectsListColumn<AdminCustomerInvitation>[] = useMemo(() => {
    const contractor = (i: AdminCustomerInvitation) => i.companyName || t('adminCustomerInvitations.noContractor');
    // Without a staff registration the contractor invited the client.
    const registeredBy = (i: AdminCustomerInvitation) =>
      i.registeredByUserId
        ? i.registeredByName || t('adminCompanyRegistrations.noName')
        : t('adminCustomerInvitations.byContractor');
    return [
      { field: 'customerName', label: t('adminCustomerInvitations.columns.customerName'), render: i => i.customerName },
      { field: 'companyName', label: t('adminCustomerInvitations.columns.companyName'), sortValue: contractor, filterValue: contractor, render: i => i.companyName || <span className="text-muted">{contractor(i)}</span> },
      {
        field: 'registeredBy',
        label: t('adminCustomerInvitations.columns.registeredBy'),
        sortValue: registeredBy,
        filterValue: registeredBy,
        render: i => (
          <>
            {registeredBy(i)}
            {i.registrarRole && <div className="small text-muted">{t(`adminCompanyRegistrations.registrarRole.${i.registrarRole}`)}</div>}
          </>
        )
      }
    ];
  }, [t]);

  return (
    <AdminInvitationsList<AdminCustomerInvitation>
      i18nPrefix="adminCustomerInvitations"
      breadcrumb={[
        { label: t('nav.home'), path: '/admin/home' },
        { label: t('appNav.customers'), path: '/admin/customers' },
        { label: t('adminCustomerInvitations.title') }
      ]}
      load={adminApi.getAllCustomerInvitations}
      preferenceKey="adminCustomerInvitationListColumns"
      defaultColumnOrder={DEFAULT_COLUMN_ORDER}
      ownColumns={ownColumns}
      nameSort={{ field: 'customerName', label: t('adminCustomerInvitations.sort.nameAZ') }}
      cardTitle={i => i.customerName}
      cardLines={i => (
        <>
          <div className="text-muted small text-truncate"><i className="feather-briefcase me-1"></i>{i.companyName || t('adminCustomerInvitations.noContractor')}</div>
          <div className="text-muted small text-truncate">
            <i className="feather-user-check me-1"></i>
            {i.registeredByUserId
              ? t('adminCustomerInvitations.registeredByStaff', { name: i.registeredByName || t('adminCompanyRegistrations.noName') })
              : t('adminCustomerInvitations.byContractor')}
          </div>
        </>
      )}
      exportName="customer_invitations"
      testIds={{ table: 'admin-customer-invitations-table', cards: 'admin-customer-invitations-cards' }}
    />
  );
}
